import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function ConstructionTable16({ data = [] }) {
  const pdfRef = useRef();

  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  // Prepare chart data
  const years = data.map((d) => d.year);
  const values = data.map((d) => parseFloat(d.bachelor_percent || 0));

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Bachelor’s Degree (%)",
        data: values,
        borderColor: "#F8BBD0",
        backgroundColor: "rgba(248, 187, 208, 0.3)",
        fill: true,
        tension: 0.3,
        pointBackgroundColor: "#F8BBD0",
        pointRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "🎓 Bachelor’s Degree Percentage (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw}%`,
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" } },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Percentage (%)" },
        ticks: { callback: (value) => `${value}%` },
      },
    },
  };

  // 📊 Summary Insights
  const first = values[0];
  const last = values[values.length - 1];
  const growth = ((last - first) / first) * 100;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const maxYearIndex = values.indexOf(Math.max(...values));

  const summary = {
    growth: growth.toFixed(1),
    avg: avg.toFixed(1),
    peakYear: years[maxYearIndex],
    peakValue: values[maxYearIndex],
  };

  // 🖨️ PDF Export
const downloadPDF = () => {
  const input = pdfRef.current;
  html2canvas(input, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    const addHeader = () => {
      const logoWidth = 20; // ideal header width
      const logoHeight = 20; // adjust ratio if needed

      pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
      pdf.line(10, 35, 200, 35);
    };

    const addFooter = (pageNumber) => {
      pdf.setFontSize(9);
      const footerY = pageHeight - 10;
      pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        10,
        footerY,
      );
      pdf.text(
        `Page ${pageNumber}`,
        pageWidth - 10,
        footerY,
        null,
        null,
        "right",
      );
    };

    // First page
    addHeader();
    pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
    heightLeft -= pageHeight - 32;

    // Add more pages if needed
    while (heightLeft > 0) {
      pdf.addPage();
      pageNumber++;
      addHeader();
      position = heightLeft - imgHeight;
      pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);
    }

    // Final footer
    // ===== FINAL FOOTER SAFE AREA =====

    const footerTopY = pageHeight - 40; // start footer 40mm from bottom
    const footerContentY = pageHeight - 28; // logos row
    const footerBottomTextY = pageHeight - 10; // bottom text row

    // Top Divider Line
    pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

    // UtahData logo
    const utahWidth = 18;
    const utahHeight = 18;
    pdf.addImage(
      utahData,
      "PNG",
      20,
      footerContentY - 8,
      utahWidth,
      utahHeight,
    );

    // Built by
    pdf.setFontSize(10);
    pdf.text("Built by", 45, footerContentY);

    // BODEX logo
    const bodexWidth = 36;
    const bodexHeight = bodexWidth * 0.3;
    pdf.addImage(bodex, "PNG", 60, footerContentY - 6, bodexWidth, bodexHeight);

    // Using text
    pdf.text("using", 102, footerContentY);

    // KPI logo
    pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

    // Version
    pdf.text(APP_VERSION, 140, footerContentY);

    // Bottom Info Line
    pdf.setFontSize(8);
    pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
    pdf.text(
      `Downloaded on: ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      footerBottomTextY,
      null,
      null,
      "right",
    );

    pdf.save("bachelor_degree_percentage_2018_2024.pdf");
  });
};

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-indigo-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Bachelor’s Degree Percentage (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-xl font-bold text-blue-800">
              +{summary.growth}%
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average (2018–2024)
            </p>
            <p className="text-xl font-bold text-green-800">{summary.avg}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-base font-semibold text-yellow-800">
              {summary.peakYear} ({summary.peakValue}%)
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Bachelor's Degree (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        row.year === summary.peakYear
                          ? "bg-yellow-50 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.bachelor_percent}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
